import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import OtpInput from "react-otp-input";
import { useForgotPasswordMutation, useValidateResetPasswordCodeMutation, useResetPasswordMutation } from "../../../../service/authentication-service";
import { ApplicationRoutes } from "../../../../routes";
import { getErrorMessage } from "../../../../utils/error-handler";
import { describeError, trackPasswordResetFailed, trackPasswordResetSuccess } from "../../../../services/business-analytics/authEvents";
import Input from "../../../../common/ekedc-components/input/Input";
import { AuthPageLayout } from "../../components";
import { ChangePasswordForm, CHANGE_PASSWORD_FORM_ID } from "./change-password-form";
import { maskEmail } from "./util";
import { StepFooter, StepHeader, VerifiedStepCard } from "./components";
import styles from "./forgot-password.module.scss";
import { useTheme } from "../../../../theme/useTheme";

type Step = 1 | 2 | 3 | 4;

const EMAIL_FORM_ID = "forgot-password-email-form";
const RESEND_TIMER_SECONDS = 180;

interface EmailFormData {
	email: string;
}

export const CustomerForgotPasswordPage = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [currentStep, setCurrentStep] = useState<Step>(1);
	const [email, setEmail] = useState("");
	const [token, setToken] = useState("");

	const [forgotPassword, { isLoading: isSendingEmail }] = useForgotPasswordMutation();
	const [validateCode, { isLoading: isValidatingOtp }] = useValidateResetPasswordCodeMutation();
	const [resetPassword, { isLoading: isResettingPassword }] = useResetPasswordMutation();
	const { setOverrideMode } = useTheme();

	useEffect(() => {
		setOverrideMode("light");
		return () => setOverrideMode(null);
	}, [setOverrideMode]);

	const {
		control: emailControl,
		handleSubmit: handleEmailFormSubmit,
		formState: { errors: emailErrors },
	} = useForm<EmailFormData>({
		mode: "onChange",
		defaultValues: { email: "" },
	});

	const handleBack = () => {
		if (currentStep === 1) {
			navigate(-1);
		} else if (currentStep === 2) {
			setCurrentStep(1);
		} else if (currentStep === 3) {
			setToken("");
			setCurrentStep(2);
		}
	};

	const handleEmailSubmit = async (data: EmailFormData) => {
		try {
			await forgotPassword(data.email.trim()).unwrap();
			setEmail(data.email);
			setCurrentStep(2);
		} catch (err: any) {
			toast.error(getErrorMessage(err?.data));
		}
	};

	const handleOtpSubmit = async () => {
		try {
			await validateCode({ token, email }).unwrap();
			setCurrentStep(3);
		} catch (err: any) {
			// A rejected code is a failed reset from the customer's side: the new password is
			// never reached, so this is the only failure this page can realistically record.
			trackPasswordResetFailed("email", describeError(err));
			toast.error(getErrorMessage(err?.data));
		}
	};

	const handleResendOtp = async () => {
		try {
			await forgotPassword(email).unwrap();
			toast.success(t("auth.web.tokenResent"));
		} catch (err: any) {
			toast.error(getErrorMessage(err?.data));
		}
	};

	const handlePasswordReset = async (newPassword: string) => {
		try {
			await resetPassword({ password: newPassword, token, email }).unwrap();
			trackPasswordResetSuccess("email");
			setCurrentStep(4);
		} catch (err: any) {
			trackPasswordResetFailed("email", describeError(err));
			toast.error(getErrorMessage(err?.data));
		}
	};

	const handleSignInWithNewPassword = () => {
		navigate(`../${ApplicationRoutes.CUSTOMER_SIGNIN}`);
	};

	const renderEmailCard = () => (
		<div className={styles.card}>
			<div className={styles.cardHeader}>
				<p className={styles.cardTitle}>{t("auth.resetPasswordInitialTitle")}</p>
				<p className={styles.cardSubtitle}>{t("auth.resetPasswordInitialSubtitle")}</p>
			</div>
			{isSendingEmail ? (
				<div className={styles.verifyingRow}>
					<div className={styles.spinner} />
					<span className={styles.verifyingText}>{t("common.labels.inputLoadingLabel")}</span>
				</div>
			) : (
				<form id={EMAIL_FORM_ID} onSubmit={handleEmailFormSubmit(handleEmailSubmit)} noValidate>
					<Controller
						name="email"
						control={emailControl}
						rules={{
							required: t("corporate.emailRequired"),
							pattern: {
								value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
								message: t("corporate.enterValidEmail"),
							},
						}}
						render={({ field }) => <Input {...field} type="email" placeholder={t("auth.web.registeredEmailPlaceholder")} error={emailErrors.email?.message} />}
					/>
				</form>
			)}
		</div>
	);

	const renderOtpCard = () => (
		<div className={styles.card}>
			<div className={styles.cardHeader}>
				<p className={styles.cardTitle}>
					{t("auth.enterOtpSentToEmail")} <span className={styles.maskedEmail}>"{maskEmail(email)}"</span>
				</p>
			</div>
			{isValidatingOtp ? (
				<div className={styles.verifyingRow}>
					<div className={styles.spinner} />
					<span className={styles.verifyingText}>{t("common.labels.inputLoadingLabel")}</span>
				</div>
			) : (
				<>
					<OtpInput
						value={token}
						onChange={otp => setToken(otp.toUpperCase())}
						numInputs={6}
						inputType="text"
						shouldAutoFocus
						containerStyle={styles.otpRow}
						renderSeparator={index => (index === 2 ? <span className={styles.otpSeparator} /> : <span className={styles.otpGap} />)}
						renderInput={props => <input {...props} className={styles.otpInputBox} />}
					/>
					<ResendRow onResend={handleResendOtp} />
				</>
			)}
		</div>
	);

	const renderSuccessCard = () => (
		<div className={styles.successCard}>
			<svg className={styles.successIcon} width="48" height="48" viewBox="0 0 48 48" fill="none">
				<circle cx="24" cy="24" r="24" fill="#2FBF2B" />
				<path d="M14 24.5L21 31.5L34 18" stroke="var(--app-base-white)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
			</svg>
			<p className={styles.successTitle}>{t("auth.web.newPasswordCreated")}</p>
			<p className={styles.successSubtitle}>{t("auth.proceedToSignin")}</p>
		</div>
	);

	const footer = (() => {
		if (currentStep === 1) {
			return <StepFooter text={t("common.actions.continueKey")} type="submit" form={EMAIL_FORM_ID} loading={isSendingEmail} loadingText={t("auth.web.verifying")} />;
		}
		if (currentStep === 2) {
			return <StepFooter text={t("common.actions.continueKey")} onClick={handleOtpSubmit} disabled={token.length !== 6} loading={isValidatingOtp} loadingText={t("auth.web.verifying")} />;
		}
		if (currentStep === 3) {
			return <StepFooter text={t("common.actions.continueKey")} type="submit" form={CHANGE_PASSWORD_FORM_ID} loading={isResettingPassword} loadingText={t("auth.web.saving")} />;
		}
		return <StepFooter text={t("auth.signInWithNewPassword")} onClick={handleSignInWithNewPassword} />;
	})();

	return (
		<AuthPageLayout>
			<div className={styles.page}>
				<StepHeader title={t("auth.resetPassword")} onBack={currentStep === 4 ? undefined : handleBack} />
				<div className={styles.body}>
					<div className={styles.stack}>
						{currentStep >= 3 && <VerifiedStepCard label={t("auth.emailAddressVerified")} />}
						{currentStep >= 3 && <VerifiedStepCard label={t("auth.otpVerified")} />}
						{currentStep === 1 && renderEmailCard()}
						{currentStep === 2 && renderOtpCard()}
						{currentStep === 3 && <ChangePasswordForm onSubmit={handlePasswordReset} />}
						{currentStep === 4 && renderSuccessCard()}
					</div>
				</div>
				{footer}
			</div>
		</AuthPageLayout>
	);
};

const ResendRow = ({ onResend }: { onResend: () => void }) => {
	const { t } = useTranslation();
	const [secondsLeft, setSecondsLeft] = useState(RESEND_TIMER_SECONDS);
	const intervalRef = useRef<ReturnType<typeof setInterval>>();

	useEffect(() => {
		intervalRef.current = setInterval(() => {
			setSecondsLeft(prev => (prev > 0 ? prev - 1 : 0));
		}, 1000);
		return () => clearInterval(intervalRef.current);
	}, []);

	const handleClick = () => {
		if (secondsLeft > 0) return;
		onResend();
		setSecondsLeft(RESEND_TIMER_SECONDS);
	};

	const minutes = Math.floor(secondsLeft / 60);
	const seconds = secondsLeft % 60;
	const formatted = `${minutes}:${seconds.toString().padStart(2, "0")}`;

	return (
		<div className={styles.otpHelper}>
			<span>{t("common.labels.didntReceiveCode")}</span>
			<button type="button" className={styles.resendButton} disabled={secondsLeft > 0} onClick={handleClick}>
				{secondsLeft > 0 ? `${t("auth.web.resendCodeIn")} ${formatted}` : t("common.labels.resendCodeBtn")}
			</button>
		</div>
	);
};
